/**
 * Bulk packing: from servings to containers, by volume (PROD-1500, logistics Phase 1; Abner, Oct 16, D26–D28; no
 * standard package and no new columns since Oct 19, D46).
 *
 * The catalog team gives each bulk package a capacity and each portion a volume; the system works out how a
 * delivery's servings break into packages. Everything is measured in CUPS, read from fields the catalog already had:
 *
 * - A BULK package's capacity is its `Ingredient.supplyShape.dimensions.volumeMl` (millilitres, like every container
 *   volume in the catalog), read here as cups. It is entered as a volume, never as a share of another package
 *   (Sheila, Oct 20, D55: "remove the % setting from pan").
 * - A portion's cups per serving come, in this order, from:
 *   1. its product's BULK_TRAY `ComponentPackaging` row for the portion's unit (`portionUnitKey`: "Taco" and "Tacos"
 *      are one unit): the capacity of the package it was measured in (`packageVolumeMl`, a snapshot) over how many of
 *      ONE unit fit in it (`portionsPerPackage`), times the serving's quantity. "20 tacos per pan" makes 1 Taco a
 *      twentieth of a pan and 2 Tacos a tenth. A row also overrides a volume unit's cups;
 *   2. a volume unit, or a `#N disher` / `#N scoop`, which is 4/N cups: ½ cup is ½ cup;
 *   3. nothing else: the portion needs a volume (PORTION_NO_VOLUME). A density is never guessed.
 * - Servings per container = ⌊container cups ÷ Σ food cups per serving⌋: a card's foods add up (a condiment riding
 *   with a food takes room too), each weighted by how many of it ride in one serving. A small relative tolerance
 *   absorbs volumes stored to 0.01 ml (a ⅓ pan, a snapshot): a ⅓ pan × 30 per pan stays 10, not 9.
 *
 * Filling (Matt and Abner, Oct 20, D54; supersedes D26's even split): every container goes out full, and the servings
 * left over ride on an "items remaining" sticker, "17 pans x20 pc + 5 EA" (Matt): the kitchen adds them to a container
 * that still has room, or uses one more. 345 at 20 → seventeen 20s + 5 EA; fewer than one container's worth is that one
 * container: 8 at 20 → one container with 8. Callers sum the lines that may share containers BEFORE planning, grouped
 * by {@link bulkPackingGroupKey} (the same container, servings per container and foods): OLHSA's 330 + 5 corn in two
 * meals is one plan, while beef tacos and Halal tacos at the same 20 per pan never share a pan.
 *
 * Never a wrong count (D23): `resolveBulkPackingSpec` returns no spec, with the gaps that block it, unless every
 * piece is known. Chef screens then keep the servings-only display.
 *
 * Lives in `@ordo/constants` because the ordo CLIENT plans packing in the browser and the Menus client previews it;
 * neither can import `@ordo/schema`. No runtime dependencies beyond `./measures`.
 */

import { isVolumeUnit, isWeightUnit, normalizeMeasureUnit, volumeToCups } from './measures'

/** Largest capacity a bulk package may claim, in cups (a full 6-inch hotel pan holds about 80). */
export const BULK_PACKAGE_MAX_CAPACITY_CUPS = 1000
/** Largest number of servings a portion may claim to fit in one package (condiment packets run into the hundreds). */
export const PORTION_MAX_SERVINGS_PER_PACKAGE = 100_000
/** The serving count every preview speaks of ("If a school orders 100 servings…"). */
export const BULK_PACKING_PREVIEW_SERVINGS = 100
/** `ComponentPackaging.calculationMode` of a portion's bulk packing row (Plan 28's "divides by package capacity"). */
export const BULK_TRAY_CALCULATION_MODE = 'BULK_TRAY'

const EPSILON = 1e-9
/**
 * Relative tolerance on a container's servings: absorbs volumes stored to 0.01 ml (a package's `volumeMl`, a row's
 * `packageVolumeMl`) and counts stored to 4 decimals, so a ⅓ pan × 30 per pan stays 10 and 49 cups of ½ cup stay 98.
 */
const SHARE_TOLERANCE = 1e-5

function round(value: number, places: number): number {
  const f = 10 ** places
  return Math.round(value * f) / f
}

/**
 * A number from what a caller holds: a number, a numeric string, or a Prisma `Decimal` (anything with `toNumber`).
 * Null for anything else, NaN or infinity.
 */
export function packingNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null
  }
  let n: number
  if (typeof value === 'number') {
    n = value
  } else if (typeof value === 'string') {
    n = value.trim().length > 0 ? Number(value) : Number.NaN
  } else if (typeof value === 'object' && typeof (value as { toNumber?: unknown }).toNumber === 'function') {
    n = (value as { toNumber: () => number }).toNumber()
  } else {
    return null
  }
  return Number.isFinite(n) ? n : null
}

function positiveOrNull(value: unknown): number | null {
  const n = packingNumber(value)
  return n !== null && n > 0 ? n : null
}

/**
 * Whole servings, floored as servings per container are: ¾ cup in a 49-cup pan is 65 (65.33). The shared tolerance
 * keeps a value a storage rounding below a whole number on it (49 cups stored as 11,592.81 ml, ÷ ½ cup, is 98).
 */
export function wholeServings(value: number): number {
  return Math.floor(value * (1 + SHARE_TOLERANCE) + EPSILON)
}

// --- Volumes -------------------------------------------------------------------------------------------------------

/** Cups in `ml` millilitres, by the one unit table (`measures.ts`). Null for anything but a positive number. */
export function cupsFromMl(ml: unknown): number | null {
  const n = positiveOrNull(ml)
  const cups = n === null ? null : volumeToCups(n, 'ml')
  return cups !== null && cups > 0 ? cups : null
}

/**
 * Millilitres in `cups`, to 0.01 ml: how a bulk package's capacity is stored (`supplyShape.dimensions.volumeMl`), at
 * the precision `ComponentPackaging.packageVolumeMl` (Decimal(10, 2)) keeps a snapshot of it, so a portion measured
 * in a package reads back exactly against that package.
 */
export function mlFromCups(cups: number): number {
  return round(cups / (volumeToCups(1, 'ml') as number), 2)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** A package's stored volume (`supplyShape.dimensions.volumeMl`), in millilitres, or null when none is set. */
export function supplyShapeVolumeMl(supplyShape: unknown): number | null {
  const dimensions = isRecord(supplyShape) && isRecord(supplyShape['dimensions']) ? supplyShape['dimensions'] : null
  return positiveOrNull(dimensions?.['volumeMl'])
}

/** A package's capacity in cups, from its `supplyShape` (`dimensions.volumeMl`); null when none is set. */
export function supplyShapeCapacityCups(supplyShape: unknown): number | null {
  return cupsFromMl(supplyShapeVolumeMl(supplyShape))
}

// --- Portions ------------------------------------------------------------------------------------------------------

const DISHER_UNIT = /^#\s*(\d+(?:\.\d+)?)\s*(?:disher|scoop)s?$/

/**
 * Cups in one serving of a portion, from its unit alone, or null when the unit is not a volume. A volume unit converts
 * directly (`cup`, `tbsp`, `fl oz`, `ml`, `qt`…); a `#N disher` / `#N scoop` unit is 4/N cups (N scoops per quart:
 * #8 = ½ cup, #16 = ¼ cup). Weights (`oz`, `g`) and counts (`each`, `taco`) are not volumes.
 */
export function cupsPerPortion(portion: { quantity: unknown; unit: string | null | undefined }): number | null {
  const quantity = positiveOrNull(portion.quantity)
  const unit = portion.unit?.trim() ?? ''
  if (quantity === null || unit.length === 0) {
    return null
  }
  if (isVolumeUnit(unit)) {
    const cups = volumeToCups(quantity, unit)
    return cups !== null && cups > 0 ? cups : null
  }
  const disher = DISHER_UNIT.exec(normalizeMeasureUnit(unit))
  if (disher) {
    const size = Number(disher[1])
    return size > 0 ? (quantity * 4) / size : null
  }
  return null
}

/** True when the portion's unit is a volume (cups, a volume unit or a disher): it needs no number, but takes one as an override. */
export function isVolumetricPortion(portion: { quantity: unknown; unit: string | null | undefined }): boolean {
  return cupsPerPortion(portion) !== null
}

/**
 * A free-text unit as bulk packing keys it: normalized and singular, so "Taco" and "Tacos" (or "Sandwich" and
 * "Sandwiches") are one unit. Units are typed by hand, and a portion edited from 1 to 2 often gains an "s". The key
 * of a portion's `ComponentPackaging` row.
 */
export function portionUnitKey(unit: string | null | undefined): string {
  const normalized = normalizeMeasureUnit(unit ?? '')
  if (/(ch|sh|x|z|ss)es$/.test(normalized)) {
    return normalized.slice(0, -2)
  }
  if (/[^s]s$/.test(normalized)) {
    return normalized.slice(0, -1)
  }
  return normalized
}

/** A portion's BULK_TRAY `ComponentPackaging` row, as the math reads it. */
export interface PortionPackingRow {
  /** The unit as authored ("Tacos"); the row measures every portion of this unit (`portionUnitKey`). */
  portionUnit: string
  /** How many of ONE unit fit in the package it was measured in: 20 tacos. */
  portionsPerPackage: unknown
  /** That package's capacity in millilitres when the number was entered (a snapshot). */
  packageVolumeMl: unknown
}

/**
 * Per unit (`portionUnitKey`), the row that measures it: the newest (`updatedAt`, then `id`) when two rows name one
 * unit, so a read never depends on row order. Pass LIVE BULK_TRAY rows only.
 */
export function packingRowsByUnit<R extends PortionPackingRow & { id: string; updatedAt: Date | string }>(
  rows: ReadonlyArray<R>,
): Map<string, R> {
  const time = (row: R) => new Date(row.updatedAt).getTime()
  const byUnit = new Map<string, R>()
  for (const row of rows) {
    const key = portionUnitKey(row.portionUnit)
    const current = byUnit.get(key)
    if (!current || time(row) > time(current) || (time(row) === time(current) && row.id < current.id)) {
      byUnit.set(key, row)
    }
  }
  return byUnit
}

/** Live rows per product (`componentId`), then per unit (`packingRowsByUnit`: the newest row wins a unit). */
export function packingRowsByComponent<
  R extends PortionPackingRow & { id: string; componentId: string; updatedAt: Date | string },
>(rows: ReadonlyArray<R>): Map<string, Map<string, R>> {
  const byComponent = new Map<string, R[]>()
  for (const row of rows) {
    const list = byComponent.get(row.componentId) ?? []
    list.push(row)
    byComponent.set(row.componentId, list)
  }
  return new Map([...byComponent].map(([componentId, list]) => [componentId, packingRowsByUnit(list)]))
}

/** Cups ONE unit takes by its row: the package's cups ÷ how many units fit in it. Null unless both are positive. */
export function packingRowCupsPerUnit(
  row: Pick<PortionPackingRow, 'portionsPerPackage' | 'packageVolumeMl'> | null | undefined,
): number | null {
  const units = positiveOrNull(row?.portionsPerPackage)
  const cups = cupsFromMl(row?.packageVolumeMl)
  return units === null || cups === null ? null : cups / units
}

/** PACKAGE: from the portion's row (how many fit in a package); UNIT: from its volume unit. */
export type PortionVolumeSource = 'PACKAGE' | 'UNIT'

export interface PortionVolume {
  cupsPerServing: number
  source: PortionVolumeSource
}

/**
 * The volume one serving of a portion takes: its row first (`packing`, which also overrides a volume unit's cups),
 * else its volume unit, else null (it needs a volume). A row for another unit is ignored.
 */
export function portionVolume(portion: {
  quantity: unknown
  unit: string | null | undefined
  packing?: Pick<PortionPackingRow, 'portionUnit' | 'portionsPerPackage' | 'packageVolumeMl'> | null
}): PortionVolume | null {
  const quantity = positiveOrNull(portion.quantity)
  const row =
    portion.packing && portionUnitKey(portion.packing.portionUnit) === portionUnitKey(portion.unit)
      ? portion.packing
      : null
  const perUnit = packingRowCupsPerUnit(row)
  if (perUnit !== null && quantity !== null) {
    return { cupsPerServing: perUnit * quantity, source: 'PACKAGE' }
  }
  const cups = cupsPerPortion(portion)
  return cups === null ? null : { cupsPerServing: cups, source: 'UNIT' }
}

/** Recipe-authored packing volume takes precedence over legacy product/unit rows.
 * Multipliers are ratios to the recipe base, not extra food quantities.
 */
export interface RecipePackingServing {
  quantity: unknown
  unit: string
  baseMultiplier?: unknown
  measures?: ReadonlyArray<{ quantity: unknown; unit: string; source: string }>
}

export function recipePortionVolume(
  serving: RecipePackingServing,
  primary: RecipePackingServing | undefined,
  legacy?: PortionPackingRow | null,
): PortionVolume | null {
  if (primary) {
    const canonical = primary.measures?.find((m) => m.source === 'BULK_PACKING')
    const equivalence = canonical ?? primary.measures?.find((m) => cupsPerPortion(m) !== null)
    const base = equivalence ? portionVolume(equivalence) : portionVolume({ ...primary, packing: legacy })
    const multiplier = positiveOrNull(serving.baseMultiplier)
    const baseMultiplier = positiveOrNull(primary.baseMultiplier) ?? 1
    if ((equivalence || cupsPerPortion(primary) !== null) && base && multiplier !== null) {
      return {
        cupsPerServing: (base.cupsPerServing * multiplier) / baseMultiplier,
        source: canonical ? 'PACKAGE' : base.source,
      }
    }
  }
  return portionVolume({ ...serving, packing: legacy })
}

/** How many servings of `cupsPerServing` fit in `capacityCups`, not floored (the count fields show 2 decimals). */
export function servingsInCapacity(cupsPerServing: unknown, capacityCups: unknown): number | null {
  const cups = positiveOrNull(cupsPerServing)
  const capacity = positiveOrNull(capacityCups)
  return cups === null || capacity === null ? null : capacity / cups
}

// --- Packages ------------------------------------------------------------------------------------------------------

/** A bulk package as the math reads it: its capacity in cups (`supplyShapeCapacityCups`), or null when not sized. */
export interface BulkPackageSizing {
  id: string
  name: string
  capacityCups: number | null
}

export type BulkPackageStatus = 'SIZED' | 'NOT_SIZED'

/** SIZED once the package has a capacity. */
export function bulkPackageStatus(pkg: Pick<BulkPackageSizing, 'capacityCups'>): BulkPackageStatus {
  return positiveOrNull(pkg.capacityCups) === null ? 'NOT_SIZED' : 'SIZED'
}

// --- Specs ---------------------------------------------------------------------------------------------------------

/** The container a deliverable ships in: `Ingredient.id` + `Ingredient.name` ("Full Hotel Pan"). */
export interface BulkPackingContainer {
  skuId: string
  name: string
}

/**
 * What keeps a card or a shipment line from a count:
 * - PACKAGE_NOT_SIZED: the container has no capacity;
 * - PORTION_NO_VOLUME: a portion that is not a volume has no "fits in a package" number yet;
 * - NO_PORTION: a food has no portion to measure (no recipe);
 * - PORTION_DIFFERS: a shipment line ships a portion none of the food's measured portions describe (a dated
 *   substitution rule changed it): no count rather than a guess;
 * - SERVING_LARGER_THAN_CONTAINER: one serving does not fit in the container.
 */
export type BulkPackingGapReason =
  | 'PACKAGE_NOT_SIZED'
  | 'PORTION_NO_VOLUME'
  | 'NO_PORTION'
  | 'PORTION_DIFFERS'
  | 'SERVING_LARGER_THAN_CONTAINER'

export interface BulkPackingGap {
  reason: BulkPackingGapReason
  /** The package to size (PACKAGE_NOT_SIZED, SERVING_LARGER_THAN_CONTAINER). */
  packageId?: string | null
  packageName?: string | null
  /** The food and portion to give a volume (PORTION_NO_VOLUME, NO_PORTION). */
  componentId?: string | null
  foodName?: string | null
  servingSizeId?: string | null
  portionLabel?: string | null
}

/** One food of a card or a shipment line, with the portion it is served in (null = none known). */
export interface BulkPackingFoodInput {
  componentId?: string | null
  name: string
  portion: {
    servingSizeId?: string | null
    label?: string | null
    quantity: unknown
    unit: string | null | undefined
    /**
     * The food's product's BULK_TRAY row for this portion's unit, when it has one: it measures the portion, and
     * overrides a volume unit's cups (Abner, Oct 19: an "override" for a volume).
     */
    packing?: Pick<PortionPackingRow, 'portionUnit' | 'portionsPerPackage' | 'packageVolumeMl'> | null
  } | null
  /** Why `portion` is null: NO_PORTION (the default) or PORTION_DIFFERS. */
  missingPortionReason?: 'NO_PORTION' | 'PORTION_DIFFERS'
  /**
   * How many of this food's portions ride in ONE serving of the deliverable (default 1): "2 ketchup packets" per meal
   * is 2; two same-kind picks sharing 25 servings as 13 + 12 are 13/25 and 12/25. Each food's cups are weighted by it.
   */
  quantityPerServing?: number
}

/** How one food was measured, for detail panels and agent tools. */
export interface BulkPackingFoodBasis {
  componentId: string | null
  name: string
  servingSizeId: string | null
  portionLabel: string | null
  /** Cups in one serving of this portion: from its row (`source` PACKAGE) or its volume unit (UNIT). */
  cupsPerServing: number
  source: PortionVolumeSource
  /** Portions of this food in one serving of the deliverable (see `BulkPackingFoodInput.quantityPerServing`). */
  quantityPerServing: number
  /** Cups this food takes per serving of the deliverable: `cupsPerServing` × `quantityPerServing`. */
  cups: number
  /** The portion as served ("2 Tacos", "½ cup"): what a leftover is counted in. Absent on older callers. */
  portionQuantity?: number | null
  portionUnit?: string | null
}

/** How a deliverable packs: its container and how many servings fit in one. Read live; never stored on lines. */
export interface BulkPackingSpec {
  container: BulkPackingContainer
  servingsPerContainer: number
  /** The container's capacity in cups. */
  containerCapacityCups: number
  foods: BulkPackingFoodBasis[]
}

function gapKey(gap: BulkPackingGap): string {
  return [gap.reason, gap.packageId ?? '', gap.componentId ?? '', gap.servingSizeId ?? ''].join('|')
}

function pushGap(gaps: BulkPackingGap[], gap: BulkPackingGap): void {
  const key = gapKey(gap)
  if (!gaps.some((existing) => gapKey(existing) === key)) {
    gaps.push(gap)
  }
}

/**
 * How many servings of `foods` fit in one `container`, or the gaps that keep it from being known. `container` is the
 * stored package with its capacity (null when the line names none). Each food is measured by `portionVolume`: its row,
 * else its volume unit; any other portion has PORTION_NO_VOLUME. Milk, beverages and anything else that does not ride
 * in the container must be left out by the caller; with no food there is no spec and no gap.
 */
export function resolveBulkPackingSpec(input: {
  container: BulkPackageSizing | null
  foods: ReadonlyArray<BulkPackingFoodInput>
}): { spec: BulkPackingSpec | null; gaps: BulkPackingGap[] } {
  const { container, foods } = input
  const gaps: BulkPackingGap[] = []
  if (foods.length === 0) {
    return { spec: null, gaps }
  }
  if (!container) {
    pushGap(gaps, { reason: 'PACKAGE_NOT_SIZED', packageId: null, packageName: null })
    return { spec: null, gaps }
  }

  const capacity = positiveOrNull(container.capacityCups)
  if (capacity === null) {
    pushGap(gaps, { reason: 'PACKAGE_NOT_SIZED', packageId: container.id, packageName: container.name })
  }

  const basis: BulkPackingFoodBasis[] = []
  for (const food of foods) {
    const portion = food.portion
    const ids = {
      componentId: food.componentId ?? null,
      foodName: food.name,
      servingSizeId: portion?.servingSizeId ?? null,
      portionLabel: portion?.label ?? null,
    }
    if (!portion) {
      pushGap(gaps, { reason: food.missingPortionReason ?? 'NO_PORTION', ...ids })
      continue
    }
    const volume = portionVolume(portion)
    if (!volume) {
      pushGap(gaps, { reason: 'PORTION_NO_VOLUME', ...ids })
      continue
    }
    const quantityPerServing = positiveOrNull(food.quantityPerServing ?? 1) ?? 1
    basis.push({
      componentId: ids.componentId,
      name: food.name,
      servingSizeId: ids.servingSizeId,
      portionLabel: ids.portionLabel,
      cupsPerServing: volume.cupsPerServing,
      source: volume.source,
      quantityPerServing,
      cups: volume.cupsPerServing * quantityPerServing,
      portionQuantity: positiveOrNull(portion.quantity),
      portionUnit: portion.unit?.trim() || null,
    })
  }

  if (gaps.length > 0 || capacity === null) {
    return { spec: null, gaps }
  }

  const totalCups = basis.reduce((sum, food) => sum + food.cups, 0)
  const servingsPerContainer = wholeServings(capacity / totalCups)
  if (servingsPerContainer < 1) {
    pushGap(gaps, {
      reason: 'SERVING_LARGER_THAN_CONTAINER',
      packageId: container.id,
      packageName: container.name,
    })
    return { spec: null, gaps }
  }
  return {
    spec: {
      container: { skuId: container.id, name: container.name },
      servingsPerContainer,
      containerCapacityCups: capacity,
      foods: basis,
    },
    gaps,
  }
}

/** "98 servings per Full Hotel Pan" / "1 serving per Half Hotel Pan". */
export function formatServingsPerContainer(servingsPerContainer: number, containerName: string): string {
  const servings = round(servingsPerContainer, 2)
  return `${servings} ${servings === 1 ? 'serving' : 'servings'} per ${containerName.trim()}`
}

// --- Filling and plans ---------------------------------------------------------------------------------------------

/** How a delivery's servings fill its containers (D54). */
export interface ContainerFill {
  /** Servings in each container: every one full, or the one container that holds fewer than a container's worth. */
  containers: number[]
  /** Servings left over once every container is full, for the "items remaining" sticker ("+ 5 EA"). 0 when none. */
  extra: number
}

/**
 * Fill containers of `perContainer` with `total` servings (Matt and Abner, Oct 20, D54): every container full, the
 * rest left over as `extra` for an "items remaining" sticker, which goes in a container with room or one more.
 * 345/20 → seventeen 20s + 5; 340/20 → seventeen 20s; 25/20 → one 20 + 5; 8/20 → one container with 8 and nothing
 * left over (there is no full container to add a sticker to). No containers for a non-positive total or capacity.
 */
export function fillContainers(total: number, perContainer: number): ContainerFill {
  if (!Number.isFinite(total) || !Number.isFinite(perContainer) || total <= 0 || perContainer <= 0) {
    return { containers: [], extra: 0 }
  }
  const full = Math.floor(total / perContainer + EPSILON)
  if (full < 1) {
    return { containers: [round(total, 6)], extra: 0 }
  }
  const rest = round(total - full * perContainer, 6)
  return { containers: Array.from({ length: full }, () => perContainer), extra: rest > EPSILON ? rest : 0 }
}

/** The unit the leftover servings are counted in: "+ 5 EA" (Matt, Oct 20: "17 pans x20 pc + 5 EA"). */
export const EXTRA_SERVINGS_UNIT = 'EA'

const ES_ENDINGS = /(x|sh|ch|ss|z)$/i

/**
 * "Bulk Tray" → "Bulk Trays", "Bulk Box" → "Bulk Boxes". Only the head noun takes the plural: the name up to its first
 * comma or opening parenthesis, so a size after it stays put ("Full Hotel Pan, 2.5"" → "Full Hotel Pans, 2.5"",
 * "Full Hotel Pan (2.5")" → "Full Hotel Pans (2.5")", "Paper Food Cup with Lid (8 oz)" → "…Lids (8 oz)").
 */
export function pluralizeContainerName(name: string): string {
  const trimmed = name.trim()
  const cut = trimmed.search(/\s*[,(]/)
  const head = cut === -1 ? trimmed : trimmed.slice(0, cut)
  const tail = cut === -1 ? '' : trimmed.slice(cut)
  if (head.length === 0) {
    return name
  }
  if (ES_ENDINGS.test(head)) {
    return `${head}es${tail}`
  }
  if (/s$/i.test(head)) {
    return `${head}${tail}`
  }
  return `${head}s${tail}`
}

/** The container name for a count: singular for exactly one, plural otherwise. */
export function containerNameFor(count: number, name: string): string {
  return count === 1 ? name.trim() : pluralizeContainerName(name)
}

function formatServings(servings: number): string {
  return String(round(servings, 2))
}

/**
 * What the servings left over after the full containers are counted in: the serving size's own value, whatever does
 * not fill a whole container (Sheila, Oct 22: "3 trays + X C or + 3 hamburgers", never rounded up to another
 * container). A serving of "½ cup" leaves "+ 2.5 C"; "1 Hamburger" leaves "+ 3 hamburgers"; "3 oz" leaves "+ 15 oz".
 * SERVINGS ("+ 3 EA") when the serving says nothing countable: several foods on one card, "each", or no portion.
 */
export type ExtraMeasure =
  | { kind: 'SERVINGS' }
  | { kind: 'COUNT'; perServing: number; unit: string; abbreviated?: boolean }

/** Count units that say nothing about the food: a leftover in these stays in servings ("EA"). */
const GENERIC_COUNT_UNITS = new Set([
  'each',
  'ea',
  'pc',
  'pcs',
  'piece',
  'count',
  'ct',
  'item',
  'serving',
  'portion',
  'unit',
  'package',
  'pkg',
])

const CUP_UNITS = new Set(['c', 'cup', 'cups'])

/** How a deliverable's leftover servings are counted, from the serving size of the food it packs. */
export function extraMeasureFor(
  foods: ReadonlyArray<Pick<BulkPackingFoodBasis, 'quantityPerServing' | 'portionQuantity' | 'portionUnit'>>,
): ExtraMeasure {
  if (foods.length !== 1) {
    return { kind: 'SERVINGS' }
  }
  const food = foods[0]!
  const unit = food.portionUnit?.trim() ?? ''
  if (!unit || !food.portionQuantity) {
    return { kind: 'SERVINGS' }
  }
  const perServing = food.portionQuantity * food.quantityPerServing
  const normalized = normalizeMeasureUnit(unit)
  if (CUP_UNITS.has(normalized)) {
    return { kind: 'COUNT', perServing, unit: 'C', abbreviated: true }
  }
  // Volumes and weights are abbreviations ("fl oz", "oz", "lb"): they never take a plural.
  if (isVolumeUnit(unit) || isWeightUnit(unit)) {
    return { kind: 'COUNT', perServing, unit: normalized, abbreviated: true }
  }
  const key = portionUnitKey(unit)
  if (!key || GENERIC_COUNT_UNITS.has(key)) {
    return { kind: 'SERVINGS' }
  }
  return { kind: 'COUNT', perServing, unit: key }
}

/** How one serving size counts a leftover: "½ cup" → C, "1 Taco" → tacos, "1 each" → servings (EA). */
export function extraMeasureForServing(
  serving: { quantity: unknown; unit: string | null | undefined } | null,
): ExtraMeasure {
  return serving
    ? extraMeasureFor([
        { quantityPerServing: 1, portionQuantity: positiveOrNull(serving.quantity), portionUnit: serving.unit },
      ])
    : { kind: 'SERVINGS' }
}

/** The leftover as a number and its unit word: { "2.5", "C" }, { "3", "hamburgers" }, { "3", "EA" }; null for none. */
export function extraServingsParts(
  extra: number,
  measure?: ExtraMeasure | null,
): { count: string; word: string } | null {
  if (!(extra > 0)) {
    return null
  }
  if (measure?.kind === 'COUNT') {
    const count = round(extra * measure.perServing, 2)
    return {
      count: String(count),
      word: count === 1 || measure.abbreviated ? measure.unit : pluralizeContainerName(measure.unit),
    }
  }
  return { count: formatServings(extra), word: EXTRA_SERVINGS_UNIT }
}

/** The leftover servings as every surface prints them, "+ 5 EA" / "+ 2.5 C" / "+ 3 hamburgers"; empty for none. */
export function formatExtraServings(extra: number, measure?: ExtraMeasure | null): string {
  const parts = extraServingsParts(extra, measure)
  return parts ? `+ ${parts.count} ${parts.word}` : ''
}

function withExtra(text: string, extra: number, measure?: ExtraMeasure | null): string {
  const tail = formatExtraServings(extra, measure)
  return tail ? `${text} ${tail}` : text
}

/** Runs of consecutive containers holding equal servings: [35, 35, 30] → [{ 2, 35 }, { 1, 30 }]. */
export function containerRuns(containers: ReadonlyArray<number>): Array<{ count: number; servings: number }> {
  const runs: Array<{ count: number; servings: number }> = []
  for (const servings of containers) {
    const last = runs[runs.length - 1]
    if (last && Math.abs(last.servings - servings) < EPSILON) {
      last.count += 1
    } else {
      runs.push({ count: 1, servings })
    }
  }
  return runs
}

/**
 * The chef-facing description of a run of containers: "2x Full Hotel Pans w/ 20, 1x Full Hotel Pan w/ 13",
 * "4x Bulk Delivery Boxes w/ 144, 1x Bulk Delivery Box w/ 120". Empty string for no containers.
 */
export function formatContainerRuns(containers: ReadonlyArray<number>, containerName: string): string {
  return containerRuns(containers)
    .map((run) => `${run.count}x ${containerNameFor(run.count, containerName)} w/ ${formatServings(run.servings)}`)
    .join(', ')
}

/**
 * A fill in a table cell, without the container's name: "3 × 30 + 10 EA" (100 servings at 30 a pan), "1 × 100".
 * Empty string for no containers.
 */
export function formatCompactContainerRuns(
  containers: ReadonlyArray<number>,
  extra = 0,
  measure?: ExtraMeasure | null,
): string {
  const runs = containerRuns(containers)
    .map((run) => `${run.count} × ${formatServings(run.servings)}`)
    .join(' + ')
  return runs ? withExtra(runs, extra, measure) : ''
}

/** "3 Full Hotel Pans" / "1 Full Hotel Pan". */
export function formatContainerCount(count: number, containerName: string): string {
  return `${count} ${containerNameFor(count, containerName)}`
}

/** A delivery's servings of one deliverable, planned into containers. */
export interface BulkPackingPlan {
  container: BulkPackingContainer
  servingsPerContainer: number
  totalServings: number
  /** Servings in each container, fullest first (D54): 325 at 20 → sixteen 20s. */
  containers: number[]
  /** Servings left over once the containers are full (D54): 325 at 20 → 5, "+ 5 EA". 0 when none. */
  extra: number
  /** The containers, never counting one for the leftover servings: the kitchen decides whether they need one. */
  containerCount: number
  /** "16 Full Hotel Pans + 5 EA" */
  countLabel: string
  /** "16x Full Hotel Pans w/ 20 + 5 EA" */
  description: string
  /** What `extra` is counted in (cups, the food's own count, or servings). Absent = servings ("EA"). */
  extraMeasure?: ExtraMeasure
}

function planWords(containers: ReadonlyArray<number>, extra: number, name: string, measure?: ExtraMeasure) {
  return {
    containerCount: containers.length,
    countLabel: withExtra(formatContainerCount(containers.length, name), extra, measure),
    description: containers.length > 0 ? withExtra(formatContainerRuns(containers, name), extra, measure) : '',
  }
}

/** Plan `totalServings` (already summed over every line of one deliverable in one delivery) into containers. */
export function planBulkPacking(
  totalServings: number,
  spec: Pick<BulkPackingSpec, 'container' | 'servingsPerContainer'> & Partial<Pick<BulkPackingSpec, 'foods'>>,
): BulkPackingPlan {
  const total = Number.isFinite(totalServings) && totalServings > 0 ? totalServings : 0
  const { containers, extra } = fillContainers(total, spec.servingsPerContainer)
  const extraMeasure = spec.foods ? extraMeasureFor(spec.foods) : undefined
  return {
    container: spec.container,
    servingsPerContainer: spec.servingsPerContainer,
    totalServings: total,
    containers,
    extra,
    ...(extraMeasure ? { extraMeasure } : {}),
    ...planWords(containers, extra, spec.container.name, extraMeasure),
  }
}

/**
 * What makes two plans of ONE row pack the same way: the container and the servings per container. Used to merge a
 * row's plans across delivery days or schools, where the food is already the same. To group LINES before planning, use
 * {@link bulkPackingGroupKey}, which also names the foods.
 */
export function bulkPackingSpecKey(spec: {
  container: Pick<BulkPackingContainer, 'skuId'>
  servingsPerContainer: number
}): string {
  return `${spec.container.skuId}|${spec.servingsPerContainer}`
}

/**
 * Which shipment lines may share containers: the same container, the same servings per container AND the same foods
 * (sorted distinct component ids). The same food in two meals, or two grade tiers of one food, pool into one plan
 * (OLHSA's 330 + 5 corn); two different foods never do, even when they happen to fit the same number per pan (beef
 * tacos and Halal tacos, or green beans and corn at ½ cup).
 */
export function bulkPackingGroupKey(spec: {
  container: Pick<BulkPackingContainer, 'skuId'>
  servingsPerContainer: number
  foods: ReadonlyArray<{ componentId?: string | null; name: string }>
}): string {
  const foods = [...new Set(spec.foods.map((food) => food.componentId ?? `name:${food.name}`))].sort()
  return `${bulkPackingSpecKey(spec)}|${foods.join(',')}`
}

/**
 * Merge plans that pack the same way ({@link bulkPackingSpecKey}: one per delivery day of a multi-day view, or one per
 * school on a merged row) by pooling their containers, fullest first, and adding up their leftover servings, so equal
 * containers read as one run ("32x Full Hotel Pans w/ 20 + 10 EA", never the same runs repeated day by day). Plans
 * that pack differently stay apart, in first-seen order. Never re-fills: each delivery was packed on its own.
 */
export function mergeBulkPackingPlans(plans: ReadonlyArray<BulkPackingPlan>): BulkPackingPlan[] {
  const merged = new Map<string, BulkPackingPlan>()
  for (const plan of plans) {
    const key = bulkPackingSpecKey(plan)
    const existing = merged.get(key)
    if (!existing) {
      merged.set(key, { ...plan, containers: [...plan.containers] })
      continue
    }
    const containers = [...existing.containers, ...plan.containers].sort((a, b) => b - a)
    const extra = round(existing.extra + plan.extra, 6)
    // Leftovers add up only in a shared unit; plans counted differently fall back to servings.
    const extraMeasure: ExtraMeasure | undefined =
      JSON.stringify(existing.extraMeasure ?? null) === JSON.stringify(plan.extraMeasure ?? null)
        ? existing.extraMeasure
        : { kind: 'SERVINGS' }
    merged.set(key, {
      ...existing,
      totalServings: existing.totalServings + plan.totalServings,
      containers,
      extra,
      ...(extraMeasure ? { extraMeasure } : {}),
      ...planWords(containers, extra, existing.container.name, extraMeasure),
    })
  }
  return [...merged.values()]
}
